import React, { useMemo, useState } from 'react';
import Card from '../../../../../components/material-ui/adaptive/Card';
import { Stack, Typography } from '@mui/material';
import Grid from '@mui/material/Grid2';
import { useTranslation } from 'react-i18next';
import CusInput from '../../../../../components/material-ui/CusInput';
import CusDatePicker from '../../../../../components/material-ui/CusDatePicker';
import CurrencySelect from '../../../../../components/shared/CurrencySelect';
import CusCheckbox from '../../../../../components/material-ui/CusCheckbox';
import CusButton from '../../../../../components/material-ui/CusButton';
import CusTooltip from '../../../../../components/material-ui/CusTooltip';

export default function PromoCreateNew({ group, defaultNewPromo, onCreate }) {
  const { t } = useTranslation();

  const [newBonus, setNewBonus] = useState(defaultNewPromo);

  const dateFrom = newBonus.dateFrom;
  const dateTo = newBonus.dateTo;
  const expireDate = newBonus.expireDate;

  const onCreateClick = () => onCreate(newBonus);
  const changeField = (fieldName, val) => {
    setNewBonus((prev) => ({ ...prev, [fieldName]: val }));
  };

  const changeToDeposit = () => setNewBonus((prev) => ({ ...prev, toDeposit: !prev.toDeposit }));
  const changeUseCasinoTax = () =>
    setNewBonus((prev) => ({ ...prev, useCasinoTax: !prev.useCasinoTax }));

  const tooltipTextByGroup = !group.game
    ? 'The generated promo code is valid for players of the current and ALL subsidiary groups.'
    : 'The generated promo code is valid only for players in the current Game Group.';

  const tooltipText = `${t(tooltipTextByGroup)} \n ${t(
    'The bonus replenishes the balance to the amount specified in the promo code.'
  )}`;

  const btnCreateDisabled = useMemo(() => {
    return !(newBonus.seriesName && newBonus.bonusAmount);
  }, [newBonus, newBonus]);

  return (
    <Card>
      <Stack direction={'row'} display={'flex'} gap={1} alignItems={'center'}>
        <Typography variant="h5">{t('Creating a promotional code')}</Typography>
        <CusTooltip text={tooltipText} />
      </Stack>
      <Grid container alignItems={'end'} spacing={{ xs: 1, md: 1.5 }} mt={2}>
        <Grid size={{ xs: 12, sm: 4, md: 3, xl: 1.5 }}>
          <CusInput
            label={t('Series Title')}
            value={newBonus.seriesName}
            onChange={(e) => changeField('seriesName', e.target.value)}
            maxLength={128}
          />
        </Grid>
        <Grid size={{ xs: 6, sm: 2, md: 3, xl: 1.5 }}>
          <CusInput
            label={t('Quantity')}
            tooltipText={'Number of promo codes in a series'}
            value={newBonus.totalCount}
            onChange={(e) => changeField('totalCount', +e.target.value)}
            type="number"
            inputProps={{ min: 1, max: 1000 }}
          />
        </Grid>
        <Grid size={{ xs: 6, sm: 2, md: 3, xl: 1.5 }}>
          <CusInput
            label={t('Use')}
            tooltipText={'Maximum number of uses for each promo code'}
            value={newBonus.useTimes}
            type="number"
            onChange={(e) => changeField('useTimes', +e.target.value)}
            inputProps={{ min: 1 }}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 4, md: 3, xl: 2.25 }}>
          <CusInput
            tooltipText={
              'The name is used for a single promo code. The prefix is used as a prefix for promo code series names.'
            }
            label={t('Name/Prefix')}
            value={newBonus.prefix}
            maxLength={128}
            onChange={(e) => changeField('prefix', e.target.value)}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 4, md: 4, xl: 1.75 }} mt={1}>
          <CusInput
            label={t('Amount of deposits')}
            tooltipText={
              'To activate the promo code, the player must make the minimum required deposit amount within the specified period of time.'
            }
            value={newBonus.minDeposit ?? ''}
            onChange={(e) => changeField('minDeposit', +e.target.value)}
            type="number"
            inputProps={{ min: 0 }}
          />
        </Grid>
        <Grid size={{ xs: 6, sm: 4, md: 4, xl: 1.75 }} mt={1}>
          <CusDatePicker
            clearable
            label={t('Date of deposits, from')}
            time={'00:00:00'}
            value={dateFrom}
            onChange={(val) => changeField('dateFrom', val)}
          />
        </Grid>
        <Grid size={{ xs: 6, sm: 4, md: 4, xl: 1.75 }} mt={1}>
          <CusDatePicker
            clearable
            label={t('Date of deposits, up to')}
            time={'23:59:59'}
            value={dateTo}
            onChange={(val) => changeField('dateTo', val)}
            errorText={
              dateFrom && dateTo && dateFrom > dateTo
                ? t('End date must be later than start date')
                : ''
            }
          />
        </Grid>
      </Grid>

      <Grid container mt={2} spacing={{ xs: 1, lg: 2 }} alignItems={'end'}>
        <Grid size={{ xs: 6, sm: 4, xl: 2 }}>
          <CusDatePicker
            label={t('Sell by')}
            time={'23:59:59'}
            value={expireDate}
            onChange={(val) => changeField('expireDate', val)}
          />
        </Grid>

        <Grid size={{ xs: 6, sm: 4, xl: 2 }}>
          <CusInput
            label={t('Amount bonus')}
            value={newBonus.bonusAmount || ''}
            onChange={(e) => changeField('bonusAmount', +e.target.value)}
            type="number"
            inputProps={{ min: 1 }}
          />
        </Grid>

        <Grid size={{ xs: 6, sm: 4, xl: 2 }}>
          <CurrencySelect
            value={newBonus.currency}
            setValue={(val) => changeField('currency', val)}
          />
        </Grid>

        <Grid size={{ xs: 6, sm: 4, xl: 2 }}>
          <CusInput
            label={t('Wager')}
            value={newBonus.wager ?? ''}
            onChange={(e) => changeField('wager', +e.target.value)}
            type="number"
            inputProps={{ min: 0, max: 1000 }}
          />
        </Grid>
        <Grid size={{ xs: 6, sm: 4, xl: 2 }}>
          <CusInput
            label={t('Cashout')}
            value={newBonus.cashout ?? ''}
            onChange={(e) => changeField('cashout', +e.target.value)}
            type="number"
            inputProps={{ min: 0, max: 1000000 }}
          />
        </Grid>

        <Grid size={{ xs: 6, sm: 4, xl: 2 }}>
          <CusButton
            fullWidth
            disabled={btnCreateDisabled}
            onClick={onCreateClick}
            text={'Create'}
          />
        </Grid>
      </Grid>

      <Stack display={'flex'} direction={'row'} mt={1} gap={2}>
        <CusCheckbox
          label="Credit to deposit balance"
          checked={newBonus.toDeposit}
          onChange={changeToDeposit}
        />

        <CusCheckbox
          label="In bonus calculations, reduce the deposit amount by the amount of the commission withheld from the player for the deposit."
          checked={newBonus.useCasinoTax}
          onChange={changeUseCasinoTax}
        />
      </Stack>
    </Card>
  );
}
